import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  defaultDropAnimation,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import React, { useState } from "react";
import { Button } from "../../UI/Button";
import {
  BoardSections as BoardSectionsType,
  Task,
} from "../../../models/Kanban";
import {
  findBoardSectionContainer,
  initializeBoard,
} from "../../../utils/GetBoardData";
import { getTaskById } from "../../../utils/GetTasks";
import BoardSection from "./BoardSection";
import KanbanCard from "./KanbanCard";

import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useMutation } from "react-query";
import { ToastVariant, useToastContext } from "../../../context";
import { changeCampaignStatus } from "../../../services/campaign/campaign.api";
import { KanbanBoardStatus } from "../../../types";
import { useTranslation } from "react-i18next";

type Props = {
  setIsAddCampaignVisible: React.Dispatch<React.SetStateAction<boolean>>;
  initialTaskList: Task[];
  setCampaignId: React.Dispatch<React.SetStateAction<string>>;
};

const BoardSectionList = ({
  setIsAddCampaignVisible,
  initialTaskList,
  setCampaignId,
}: Props) => {
  const tasks = initialTaskList;
  const { t } = useTranslation();

  const initialBoardSections = initializeBoard(initialTaskList);

  const [boardSections, setBoardSections] =
    useState<BoardSectionsType>(initialBoardSections);

  const [activeTaskId, setActiveTaskId] = useState<null | string>(null);

  const { addToast } = useToastContext();

  const changeStatusMutation = useMutation(changeCampaignStatus, {
    onSuccess: () => {
      window.location.reload();
    },
    onError: () => console.error("error"),
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveTaskId(active.id as string);
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    const activeContainer = findBoardSectionContainer(
      boardSections,
      active.id as string
    );

    const overContainer = findBoardSectionContainer(
      boardSections,
      over?.id as string
    );

    if (
      !activeContainer ||
      !overContainer ||
      activeContainer === overContainer ||
      (activeContainer === KanbanBoardStatus.Live &&
        overContainer === KanbanBoardStatus.Draft) ||
      (activeContainer === KanbanBoardStatus.Archived &&
        overContainer === KanbanBoardStatus.Live)
    ) {
      return;
    }

    confirmAlert({
      title: t("board.confirm_title"),
      message: t("board.confirm_message"),
      closeOnClickOutside: false,
      customUI: ({ onClose }) => {
        return (
          <div className="flex flex-col backdrop-blur border-indigo-200 border-2 p-8 rounded-md h-[25vh] justify-center">
            <h1 className="text-center font-semibold text-md">
              {t("board.confirm_message")}
            </h1>

            <div className="flex gap-2 mt-5 justify-center">
              <Button
                variant="outline"
                className="w-24 border-2"
                onClick={onClose}
              >
                {t("board.no")}
              </Button>
              <Button
                className="w-24 border-2 border-indigo-500"
                onClick={() => {
                  changeStatusMutation.mutate(
                    {
                      campaignId: active.id,
                      changeTo: overContainer,
                    },
                    {
                      onSuccess: () => {
                        setBoardSections((boardSection) => {
                          const activeItems =
                            boardSection[activeContainer as KanbanBoardStatus];

                          const overItems =
                            boardSection[overContainer as KanbanBoardStatus];

                          const activeIndex = activeItems.findIndex(
                            (item) => item.id === active.id
                          );

                          const overIndex = overItems.findIndex(
                            (item) => item.id !== over?.id
                          );

                          return {
                            ...boardSection,
                            [activeContainer]: [
                              ...boardSection[
                                activeContainer as KanbanBoardStatus
                              ].filter((item) => item.id !== active.id),
                            ],
                            [overContainer]: [
                              ...boardSection[
                                overContainer as KanbanBoardStatus
                              ].slice(0, overIndex),
                              boardSections[
                              activeContainer as KanbanBoardStatus
                              ][activeIndex],
                              ...boardSection[
                                overContainer as KanbanBoardStatus
                              ].slice(
                                overIndex,
                                boardSection[overContainer as KanbanBoardStatus]
                                  .length
                              ),
                            ],
                          };
                        });

                        addToast(ToastVariant.SUCCESS, {
                          title: t("board.status_changed"),
                        });
                      },
                      onError: () => {
                        console.error("error");

                        addToast(ToastVariant.ERROR, {
                          title: t("board.error_title"),
                          description: t("board.error_description"),
                        });
                      },
                    }
                  );
                  onClose();
                }}
              >
                {t("board.yes")}
              </Button>
            </div>
          </div>
        );
      },
      onKeypressEscape: () => {
        return;
      },
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const activeContainer = findBoardSectionContainer(
      boardSections,
      active.id as string
    ) as KanbanBoardStatus;

    const overContainer = findBoardSectionContainer(
      boardSections,
      over?.id as string
    );

    if (
      !activeContainer ||
      !overContainer ||
      activeContainer !== overContainer
    ) {
      return;
    }

    const activeIndex = boardSections[activeContainer].findIndex(
      (task) => task.id === active.id
    );
    const overIndex = boardSections[overContainer].findIndex(
      (task) => task.id === over?.id
    );

    if (activeIndex !== overIndex) {
      setBoardSections((boardSection) => ({
        ...boardSection,
        [overContainer]: arrayMove(
          boardSection[overContainer],
          activeIndex,
          overIndex
        ),
      }));
    }

    setActiveTaskId(null);
  };

  const dropAnimation: DropAnimation = {
    ...defaultDropAnimation,
  };

  const task = activeTaskId ? getTaskById(tasks, activeTaskId) : null;

  return (
    <div className="flex mt-8 h-[500px]">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="w-1/4 border-r-2 border-gray-300 ">
          <h1 className="text-center mb-7 font-bold">{t("board.new")}</h1>

          <div
            className="flex justify-center items-center h-32 border-2 mx-3 rounded-xl bg-indigo-200 hover:bg-indigo-300 cursor-pointer
                    "
            onClick={() => {
              setIsAddCampaignVisible((prev) => !prev);
            }}
          >
            <Plus />
          </div>
        </div>
        {Object.keys(boardSections).map((boardSectionKey) => {
          const isLastIndex =
            Object.keys(boardSections).length - 1 ===
            Object.keys(boardSections).indexOf(boardSectionKey);

          return (
            <BoardSection
              key={boardSectionKey}
              id={boardSectionKey}
              status={boardSectionKey as KanbanBoardStatus}
              tasks={boardSections[boardSectionKey as KanbanBoardStatus]}
              lastIndex={isLastIndex}
              setCampaignId={setCampaignId}
            />
          );
        })}
        <DragOverlay dropAnimation={dropAnimation}>
          {task ? (
            <KanbanCard
              taskId={task.id}
              key={task.id}
              name={task.name}
              startDate={task.startDate}
              endDate={task.endDate}
              draft={task.status === "draft"}
              isPermanent={task.isPermanent}
              setCampaignId={setCampaignId}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default BoardSectionList;
